import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";

import { Button, FormControlLabel, Radio, TextField, CircularProgress, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import ResetEvacuationDialog from "./components/ResetEvacuationDialog";
import ConfirmEvacuationDialog from "./components/ConfirmEvacuationDialog";
import ToggleAccountabilityDialog from "./components/ToggleAccountabilityDialog";
import { Translate, getTranslation } from "orion-components/i18n";
import { evacuationService } from "client-app-core";

import moment from "moment";
import { List as VirtualList, AutoSizer } from "react-virtualized";
import { PrintIcon, EvacuationPrintIconWhite } from "../../CBComponents/Icons";
import { getSortedAccounted, getSortedUnaccounted, selectEvacuationSettings } from "./selectors";
import { receiveProcedureInfo } from "../../Services/EvacuationAccountabilityService/actions";
import { createUserFeedback } from "orion-components/Dock/Actions/index.js";
import RenderPersonnel from "./components/RenderPersonnel";
import {
	formatTime,
	getFilteredPersonnel,
	initializeEvacuationTimer,
	startEvacuationCountdown
} from "./components/utils";
import { downloadPDF } from "../trans-utils";

const CustomRadio = styled(Radio)(({ theme }) => ({
	color: "#FFFFFF", // White for unselected
	"&.Mui-checked": {
		color: "#00BFFF" // Cyan when selected
	},
	"& .MuiSvgIcon-root": {
		fontSize: 20
	}
}));

const EvacuationAccountability = () => {
	const dispatch = useDispatch();

	const [evacuationStarted, setEvacuationStarted] = useState(false);
	const [timeLeft, setTimeLeft] = useState(0);
	const [searchTerm, setSearchTerm] = useState("");
	const [filter, setFilter] = useState("unaccounted");
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [pendingCheckedValue, setPendingCheckedValue] = useState(false);
	const [confirmReset, setConfirmReset] = useState(false);
	const [pendingPersonId, setPendingPersonId] = useState(null);
	const [isGenerating, setIsGenerating] = useState(false);
	const [isGeneratingIcon, setIsGeneratingIcon] = useState(false);
	const [showPrintRequired, setShowPrintRequired] = useState(false);

	const accounted = useSelector(getSortedAccounted);
	const unaccounted = useSelector(getSortedUnaccounted);
	const { procedureId, procedureStartTime } = useSelector(state => state.evacuationAccountability);
	const { timer = 30, timerUnit = "minutes" } = useSelector(state => selectEvacuationSettings(state));

	const locale = useSelector(state => state.i18n && state.i18n.locale);
	const timeFormatPreference = useSelector(state => state.appState.global.timeFormat);
	moment.locale(locale);

	useEffect(() => {
		if (procedureStartTime) {
			initializeEvacuationTimer(
				procedureStartTime,
				timer,
				timerUnit,
				setTimeLeft,
				setShowPrintRequired,
				setEvacuationStarted
			);
		}
	}, [procedureStartTime, timer, timerUnit]);

	useEffect(() => {
		if (!evacuationStarted) {
			setFilter("unaccounted");
		}
		const cleanup = startEvacuationCountdown(evacuationStarted, timeLeft, setTimeLeft, setShowPrintRequired);
		return cleanup;
	}, [evacuationStarted, timeLeft]);

	const handleEvacuationToggle = () => {
		if (!evacuationStarted) {
			// Create procedure before starting evacuation

			evacuationService.createProcedure({}, (err, result) => {
				if (err) {
					console.error("Procedure creation failed:", err);
					return;
				}

				setEvacuationStarted(true);

				dispatch(
					receiveProcedureInfo({
						procedureId: result.procedure.id,
						procedureStartTime: result.procedure.timerStartTimeStamp
					})
				);
			});
		} else {
			setEvacuationStarted(false);
			setTimeLeft(0);
		}
		setConfirmReset(false);
	};

	useEffect(() => {
		if (Boolean(procedureId)) setEvacuationStarted(true);
	}, [procedureId]);

	const renderPersonnel =
		filteredList =>
		({ index, key, style }) => {
			const person = filteredList[index];

			return (
				<RenderPersonnel
					person={person}
					index={index}
					key={key}
					style={style}
					timeFormatPreference={timeFormatPreference}
					setPendingCheckedValue={setPendingCheckedValue}
					setPendingPersonId={setPendingPersonId}
					setShowConfirmDialog={setShowConfirmDialog}
				/>
			);
		};

	const filteredList = getFilteredPersonnel(filter === "accounted" ? accounted : unaccounted, searchTerm);

	const handleGeneratePDF = (isIconButton = false) => {
		if (isIconButton) setIsGeneratingIcon(true);
		else setIsGenerating(true);

		evacuationService.generateProcedurePDF({ procedureId }, (err, result) => {
			isIconButton ? setIsGeneratingIcon(false) : setIsGenerating(false);

			if (err) {
				console.error("PDF generation failed:", err);
				if (isIconButton) {
					dispatch(createUserFeedback("Failed to generate evacuation report", null, true));
				}
				return;
			}

			downloadPDF(result?.data?.data, "evacuation-report.pdf");

			// Reset timer only if not icon-triggered
			if (!isIconButton) {
				evacuationService.resetTimer(procedureId, (err, result) => {
					if (err) {
						console.error("Timer reset failed:", err);
						return;
					}

					dispatch(
						receiveProcedureInfo({
							procedureId,
							procedureStartTime: result.procedure.timerStartTimeStamp
						})
					);
					setShowPrintRequired(false);
				});
			}
		});
	};

	return (
		<>
			<div className="evacuation-accountability">
				<Tooltip
					title={
						evacuationStarted
							? getTranslation("global.sharedComponents.evacuationAccountability.resetButtonToolTip")
							: getTranslation("global.sharedComponents.evacuationAccountability.activateButtonToolTip")
					} data-testid="orion-components-shared-components-evacuation-accountability-evacuation-accountability-tooltip"
				>
					<Button
						variant="contained"
						onClick={() => {
							setConfirmReset(true);
						}}
						className="evacuation-button" data-testid="orion-components-shared-components-evacuation-accountability-evacuation-accountability-button"
					>
						{evacuationStarted
							? getTranslation("global.sharedComponents.evacuationAccountability.reset")
							: getTranslation("global.sharedComponents.evacuationAccountability.active")}
					</Button>
				</Tooltip>

				{!evacuationStarted && (
					<ConfirmEvacuationDialog
						confirmReset={confirmReset}
						handleEvacuationToggle={handleEvacuationToggle}
						setConfirmReset={setConfirmReset}
					/>
				)}
				{evacuationStarted && (
					<>
						<ResetEvacuationDialog
							confirmReset={confirmReset}
							setConfirmReset={setConfirmReset}
							procedureId={procedureId}
							setEvacuationStarted={setEvacuationStarted}
						/>
						<div className="evacuation-timer-container">
							{!showPrintRequired ? (
								<>
									<div className="evacuation-timer-label">
										<Translate value="global.sharedComponents.evacuationAccountability.reportIn" />
									</div>
									<div className="evacuation-timer">{formatTime(timeLeft)}</div>
								</>
							) : (
								<>
									<div className="evacuation-timer-label">
										{getTranslation(
											"global.sharedComponents.evacuationAccountability.reportRequired"
										)}
									</div>
									{!isGenerating ? (
										<Button
											variant="outlined"
											size="small"
											startIcon={
												<span style={{ marginInlineEnd: "8px" }}>
													<EvacuationPrintIconWhite />
												</span>
											}
											className="evacuation-print-btn"
											onClick={() => handleGeneratePDF(false)}
											disabled={isGenerating}
											sx={{
												"& .MuiButton-startIcon": {
													marginRight: 0,
													marginLeft: 0
												}
											}} data-testid="orion-components-shared-components-evacuation-accountability-evacuation-accountability-button-2"
										>
											<Translate value="global.sharedComponents.evacuationAccountability.print" />
										</Button>
									) : (
										<CircularProgress size={30} data-testid="orion-components-shared-components-evacuation-accountability-evacuation-accountability-circular-progress" />
									)}
								</>
							)}
						</div>

						<div className="search-container" data-testid="orion-components-shared-components-evacuation-accountability-evacuation-accountability-div">
							<TextField
								placeholder={getTranslation("global.sharedComponents.evacuationAccountability.search")}
								variant="standard"
								fullWidth
								value={searchTerm}
								onChange={e => setSearchTerm(e.target.value)}
								style={{
									flex: 1, // takes available space
									color: "#fff",
									height: 30
								}}
								InputProps={{
									disableUnderline: false,
									sx: {
										fontSize: 12,
										fontFamily: "Roboto",
										fontStyle: "normal",
										fontWeight: 400,
										lineHeight: "normal",
										color: "#FFF",
										"& .MuiInputBase-input::placeholder": {
											color: "#949598",
											opacity: 0.7
										},
										"&:before": {
											borderBottom: "1px solid #949597"
										},
										"&:hover:not(.Mui-disabled):before": {
											borderBottom: "1px solid white"
										},
										"&:after": {
											borderBottom: "2px solid white"
										}
									}
								}} data-testid="orion-components-shared-components-evacuation-accountability-evacuation-accountability-text-field"
							/>

							<SearchIcon className="searchIcon" sx={{ color: "#696A6E", fontSize: 22 }} />
						</div>

						<div className="radio-container">
							<div style={{ width: 350 }}>
								<FormControlLabel
									style={{ width: 101 }}
									control={
										<CustomRadio
											checked={filter === "unaccounted"}
											onChange={() => setFilter("unaccounted")} data-testid="orion-components-shared-components-evacuation-accountability-evacuation-accountability-custom-radio"
										/>
									}
									label={
										<span className="radio-label">
											<Translate value="global.sharedComponents.evacuationAccountability.unaccounted" />
										</span>
									}
								/>
								<FormControlLabel
									className="accounted-label"
									control={
										<CustomRadio
											checked={filter === "accounted"}
											onChange={() => setFilter("accounted")} data-testid="orion-components-shared-components-evacuation-accountability-evacuation-accountability-custom-radio-2"
										/>
									}
									label={
										<span className="radio-label">
											<Translate value="global.sharedComponents.evacuationAccountability.accounted" />
										</span>
									}
								/>
							</div>
							{!isGeneratingIcon ? (
								<a
									className="link-wrapper"
									onClick={() => {
										handleGeneratePDF(true);
									}} data-testid="orion-components-shared-components-evacuation-accountability-evacuation-accountability-a"
								>
									<PrintIcon />
								</a>
							) : (
								<CircularProgress size={30} data-testid="orion-components-shared-components-evacuation-accountability-evacuation-accountability-circular-progress-2" />
							)}
						</div>
						{/* Virtualized List Section */}
						<div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 325px)" }}>
							<div style={{ flex: 1, minHeight: 0 }}>
								<AutoSizer>
									{({ width, height }) => (
										<VirtualList
											width={width}
											height={height}
											rowCount={filteredList.length}
											rowHeight={110}
											rowRenderer={renderPersonnel(filteredList)}
											overscanRowCount={5}
										/>
									)}
								</AutoSizer>
							</div>
						</div>
					</>
				)}
			</div>
			<ToggleAccountabilityDialog
				showConfirmDialog={showConfirmDialog}
				setShowConfirmDialog={setShowConfirmDialog}
				pendingPersonId={pendingPersonId}
				setPendingPersonId={setPendingPersonId}
				pendingCheckedValue={pendingCheckedValue}
				setPendingCheckedValue={setPendingCheckedValue}
				procedureId={procedureId}
			/>
		</>
	);
};

export default EvacuationAccountability;
